import { useState } from 'react';

/*
 * useLeftPanelCollapse — per-user, per-screen toggle for collapsing a
 * two-pane page's left rail down to a narrow TOOL ID spine. Returns:
 *   { collapsed: boolean, toggle: () => void, setCollapsed: (next) => void }
 *
 * Storage key: `tmg_${screenKey}_left_collapsed_${callerEmail}`
 * Values:      '1' = collapsed, '0' = expanded. Missing / unparseable / no
 *              callerEmail → defaults to false (expanded).
 *
 * setCollapsed is write-through: persists immediately (try/catch — no-op if
 * localStorage is unavailable or callerEmail is empty), then setState.
 * Mirrors the saveSplitRatio convention already established on the recon
 * pages (DisposalReconPage / LifecycleReconPage / AssetRegistryPage).
 * Splitter persistence and useGridState are intentionally NOT touched —
 * collapse only overrides width via a CSS class; the prior splitRatio
 * stays in its own localStorage entry untouched and is restored on expand.
 */

function getKey(callerEmail, screenKey) {
  return `tmg_${screenKey}_left_collapsed_${callerEmail || 'default'}`;
}

function loadCollapsed(callerEmail, screenKey) {
  if (!callerEmail) return false;
  try {
    return localStorage.getItem(getKey(callerEmail, screenKey)) === '1';
  } catch {
    return false;
  }
}

function persistCollapsed(callerEmail, screenKey, next) {
  if (!callerEmail) return;
  try {
    localStorage.setItem(getKey(callerEmail, screenKey), next ? '1' : '0');
  } catch {
    /* ignore — best-effort persistence, identical to saveSplitRatio's pattern */
  }
}

export function useLeftPanelCollapse(callerEmail, screenKey) {
  const [collapsed, setCollapsedState] = useState(() => loadCollapsed(callerEmail, screenKey));

  function setCollapsed(next) {
    persistCollapsed(callerEmail, screenKey, next);
    setCollapsedState(next);
  }

  function toggle() {
    setCollapsed(!collapsed);
  }

  return { collapsed, toggle, setCollapsed };
}
